/**
 * Vaktplan's filters in the toolbar (owner 2026-10-28, prototype A in filter.prototype.html): team, order and «Vis»
 * first, then the shift states as one-click chips with their counts. A state narrows the cards, and states chain:
 * a shift has to hold every one that is on. Filters never change a shift. As the plan narrows (Saker open, a laptop) the chips drop their words, then fold into
 * one «Status» menu with each state's name and count (FilterBar.module.css). A phone keeps the Filter panel (Panel.tsx FilterPanel).
 */
import { T } from '@/i18n';
import { Switch } from '@/ui';
import { NO_VALUE } from '@/ui/format';
import { Icon } from '@/ui/icons';
import * as Popover from '@radix-ui/react-popover';
import css from './FilterBar.module.css';

export function FilterBar({
  statuses,
  status,
  counts,
  onStatus,
  teams,
  team,
  onTeam,
  project,
  sorts,
  sort,
  onSort,
  view,
  active,
  onClear,
}: {
  statuses: [string, string][];
  status: Set<string>;
  /** the shifts each state would leave, chained with the states already on; undefined before the shifts answer,
   *  when a count would be an invented 0 (#272 A-02) */
  counts: Map<string, number> | undefined;
  onStatus: (k: string) => void;
  teams: { id: number; name: string }[];
  team: number | null;
  onTeam: (id: number | null) => void;
  project?:
    | {
        projects: { id: number; label: string }[];
        value: string | null;
        onChange: (v: string | null) => void;
        word: { one: string; none: string };
      }
    | undefined;
  sorts: [string, string][];
  sort: string;
  onSort: (k: string) => void;
  /** «Vis»: each a switch with its words */
  view: { key: string; label: string; on: boolean; onChange: (v: boolean) => void; count?: number }[];
  active: number;
  onClear: () => void;
}) {
  const teamName = teams.find((x) => x.id === team)?.name ?? T('Alle team');
  const sortName = T(sorts.find(([k]) => k === sort)?.[1] ?? '');
  const n = (k: string) => (counts ? (counts.get(k) ?? 0) : NO_VALUE);
  const brudd = counts?.get('brudd') ?? 0;
  const chip = (k: string, label: string) => (
    <button
      type="button"
      key={k}
      className={css.chip}
      aria-pressed={status.has(k)}
      title={T('{label}: {n} vakter', { label, n: n(k) })}
      onClick={() => onStatus(k)}
    >
      <i className={css.dot} data-k={k} />
      <span>{label}</span>
      <span className={css.n}>{n(k)}</span>
    </button>
  );
  return (
    <div className={css.bar} data-testid="filterbar">
      {teams.length > 0 && (
        <label className={`${css.chip} ${css.sel}${team != null ? ` ${css.on}` : ''}`} title={T('Team')}>
          <Icon name="users" />
          <span className={css.word2}>{teamName}</span>
          <select
            aria-label={T('Team')}
            value={team ?? ''}
            onChange={(e) => onTeam(e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">{T('Alle team')}</option>
            {teams.map((x) => (
              <option key={x.id} value={x.id}>
                {x.name}
              </option>
            ))}
          </select>
        </label>
      )}
      {project && (
        <label
          className={`${css.chip} ${css.sel}${project.value != null ? ` ${css.on}` : ''}`}
          title={project.word.one}
        >
          <Icon name="briefcase" />
          <span className={css.word2}>
            {project.value == null
              ? project.word.one
              : project.value === 'uten'
                ? project.word.none
                : (project.projects.find((p) => String(p.id) === project.value)?.label ?? '')}
          </span>
          <select
            aria-label={project.word.one}
            data-testid="uka-project"
            value={project.value ?? ''}
            onChange={(e) => project.onChange(e.target.value || null)}
          >
            <option value="">{T('Alle')}</option>
            {project.projects.map((p) => (
              <option key={p.id} value={String(p.id)}>
                {p.label}
              </option>
            ))}
            <option value="uten">{project.word.none}</option>
          </select>
        </label>
      )}
      <label className={`${css.chip} ${css.sel}`} title={T('Sorter: {s}', { s: sortName })}>
        <Icon name="list" />
        <span className={css.word2}>{sortName}</span>
        <select aria-label={T('Sorter medarbeidere')} value={sort} onChange={(e) => onSort(e.target.value)}>
          {sorts.map(([k, n]) => (
            <option key={k} value={k}>
              {T(n)}
            </option>
          ))}
        </select>
      </label>
      <Popover.Root>
        <Popover.Trigger asChild>
          <button type="button" className={css.chip} title={T('Vis i planen')}>
            <Icon name="eye" />
            <span className={css.word2}>{T('Vis')}</span>
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            className={css.pop}
            align="start"
            sideOffset={6}
            aria-label={T('Vis i planen')}
            data-testid="vfilter"
          >
            {view.map((v) => (
              <div key={v.key} className={css.sw}>
                <Switch
                  checked={v.on}
                  onChange={v.onChange}
                  label={
                    <>
                      {v.label}
                      {v.count ? <span className={css.n}> {v.count}</span> : null}
                    </>
                  }
                />
              </div>
            ))}
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
      {/* biome-ignore lint/a11y/useSemanticElements: a row of toggle buttons, not a form's fieldset */}
      <div className={css.chips} role="group" aria-label={T('Vis bare')}>
        {statuses.map(([k, label]) => chip(k, T(label)))}
      </div>
      <Popover.Root>
        <Popover.Trigger asChild>
          <button
            type="button"
            className={`${css.chip} ${css.fold}`}
            aria-pressed={status.size > 0}
            aria-label={brudd ? T('Status, {n} brudd', { n: brudd }) : undefined}
          >
            <Icon name="sliders" />
            {T('Status')}
            {status.size > 0 ? (
              <span className={css.badge}>{status.size}</span>
            ) : (
              // the folded chips' alert stays on the button: breaches are not only behind a click (#272 A-S06)
              brudd > 0 && <span className={css.alertn}>{T('{n} brudd', { n: brudd })}</span>
            )}
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content className={css.pop} align="start" sideOffset={6}>
            {statuses.map(([k, label]) => (
              <label key={k} className={css.opt}>
                <input type="checkbox" checked={status.has(k)} onChange={() => onStatus(k)} />
                <i className={css.dot} data-k={k} />
                {T(label)}
                <span className={css.n}>{n(k)}</span>
              </label>
            ))}
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
      {active > 0 && (
        <button type="button" className={css.clear} onClick={onClear}>
          {T('Fjern filtre')}
        </button>
      )}
    </div>
  );
}
